import { ShopItem, Redemption } from '../types';

const BACKEND_API = 'https://eazykeapi.gyar.top';
const WS_URL = 'wss://eazykeapi.gyar.top';
const SHOP_ITEMS_TIMEOUT_MS = 12_000;

export interface LeaderboardEntry {
    name: string;
    points: number;
    source: 'kicklet' | 'botrix' | 'local' | 'both';
    rank?: number;
    kickletRank?: number;
    watchtime?: number;
    messagesSent?: number;
}

export interface TopSpender {
    user_name: string;
    normalized: string;
    count: number;
}

export interface LeaderboardResponse {
    data: LeaderboardEntry[];
    page: number;
    pageSize: number;
    totalEntries: number;
    totalPages: number;
    searchTerm?: string;
    topSpender?: TopSpender | null;
}

export interface TrackingSnapshot {
    id: number;
    username: string;
    normalized: string;
    points: number;
    rank: number;
    kickletRank: number;
    watchtime: number;
    messagesSent: number;
    captured_at: string;
}

export interface TrackingForecast {
    pointsIn24h: number;
    pointsPerHour: number;
    confidence: 'low' | 'medium' | 'high';
    reason: string;
}

export interface TrackedUserSummary {
    username: string;
    normalized: string;
    active: boolean;
    created_at: string;
    updated_at: string;
    latest: TrackingSnapshot | null;
    historyCount: number;
    forecast: TrackingForecast | null;
}

export interface TrackingHistoryResponse {
    username: string;
    history: TrackingSnapshot[];
    latest: TrackingSnapshot | null;
    topPoints?: number;
    forecast: TrackingForecast | null;
}

export interface UserRedemptionCount {
    item_name: string;
    item_key?: string;
    count: number;
}

export interface ItemRedemptionUserCount {
    user_name: string;
    normalized: string;
    count: number;
}

export async function fetchShopItems(): Promise<ShopItem[]> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SHOP_ITEMS_TIMEOUT_MS);

    try {
        const response = await fetch(`${BACKEND_API}/api/items`, {
            signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        if (!response.ok) {
            throw new Error('Failed to fetch shop items');
        }
        return await response.json();
    } catch (error) {
        clearTimeout(timeoutId);
        if ((error as Error).name === 'AbortError') {
            console.error(`Request timeout: Server did not respond within ${SHOP_ITEMS_TIMEOUT_MS / 1000} seconds`);
            throw new Error('Server timeout');
        }
        console.error('Error fetching shop items:', error);
        throw error;
    }
}

export async function fetchRedemptions(limit: number = 5): Promise<Redemption[]> {
    try {
        const response = await fetch(`${BACKEND_API}/api/redemptions?limit=${limit}`);
        if (!response.ok) {
            throw new Error('Failed to fetch redemptions');
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching redemptions:', error);
        return [];
    }
}

export async function fetchItemRedemptions(itemName: string): Promise<Redemption[]> {
    try {
        const response = await fetch(`${BACKEND_API}/api/redemptions/item/${encodeURIComponent(itemName)}`);
        if (!response.ok) {
            throw new Error('Failed to fetch item redemptions');
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching item redemptions:', error);
        return [];
    }
}

export async function fetchUserRedemptionCounts(userName: string): Promise<UserRedemptionCount[]> {
    try {
        const response = await fetch(`${BACKEND_API}/api/redemptions/user/${encodeURIComponent(userName)}/items`);
        if (!response.ok) {
            throw new Error('Failed to fetch user redemptions');
        }
        const data = await response.json();
        return Array.isArray(data.items) ? data.items : [];
    } catch (error) {
        console.error('Error fetching user redemption counts:', error);
        return [];
    }
}

export async function fetchItemRedemptionUserCounts(itemName: string): Promise<ItemRedemptionUserCount[]> {
    try {
        const response = await fetch(`${BACKEND_API}/api/redemptions/item/${encodeURIComponent(itemName)}/users`);
        if (!response.ok) {
            throw new Error('Failed to fetch item redemption user counts');
        }
        const data = await response.json();
        return Array.isArray(data.users) ? data.users : [];
    } catch (error) {
        console.error('Error fetching item redemption user counts:', error);
        return [];
    }
}

export async function fetchUserRedemptions(userName: string, limit: number = 500): Promise<Redemption[]> {
    try {
        const response = await fetch(`${BACKEND_API}/api/redemptions/user/${encodeURIComponent(userName)}?limit=${limit}`);
        if (!response.ok) {
            throw new Error('Failed to fetch user redemptions');
        }
        const data = await response.json();
        return Array.isArray(data.redemptions) ? data.redemptions : [];
    } catch (error) {
        console.error('Error fetching user redemptions:', error);
        return [];
    }
}

export async function fetchChatStats(): Promise<number> {
    try {
        const response = await fetch(`${BACKEND_API}/api/chat-stats`);
        if (!response.ok) {
            throw new Error('Failed to fetch chat stats');
        }
        const data = await response.json();
        return data.chatMessages || 0;
    } catch (error) {
        console.error('Error fetching chat stats:', error);
        return 0;
    }
}

export async function fetchLeaderboard(page: number = 1): Promise<LeaderboardResponse> {
    try {
        const response = await fetch(`${BACKEND_API}/api/leaderboard?page=${page}`);
        if (!response.ok) {
            throw new Error('Failed to fetch leaderboard');
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching leaderboard:', error);
        return { data: [], page: 1, pageSize: 50, totalEntries: 0, totalPages: 0 };
    }
}

export async function searchLeaderboard(searchTerm: string, page: number = 1): Promise<LeaderboardResponse> {
    try {
        const response = await fetch(`${BACKEND_API}/api/leaderboard/search?q=${encodeURIComponent(searchTerm)}&page=${page}`);
        if (!response.ok) {
            throw new Error('Failed to search leaderboard');
        }
        return await response.json();
    } catch (error) {
        console.error('Error searching leaderboard:', error);
        return { data: [], page: 1, pageSize: 50, totalEntries: 0, totalPages: 0 };
    }
}

export async function fetchWatchtimeLeaderboard(page: number = 1, searchTerm: string = ''): Promise<LeaderboardResponse> {
    try {
        const params = new URLSearchParams({ page: String(page) });
        if (searchTerm.trim()) {
            params.set('q', searchTerm.trim());
        }

        const response = await fetch(`${BACKEND_API}/api/leaderboard/watchtime?${params.toString()}`);
        if (!response.ok) {
            throw new Error('Failed to fetch watchtime leaderboard');
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching watchtime leaderboard:', error);
        return { data: [], page: 1, pageSize: 50, totalEntries: 0, totalPages: 0 };
    }
}

export async function fetchTrackedUsers(): Promise<TrackedUserSummary[]> {
    try {
        const response = await fetch(`${BACKEND_API}/api/tracking/users`);
        if (!response.ok) {
            throw new Error('Failed to fetch tracked users');
        }
        const data = await response.json();
        return Array.isArray(data.users) ? data.users : [];
    } catch (error) {
        console.error('Error fetching tracked users:', error);
        return [];
    }
}

export async function startTrackingUser(username: string, turnstileToken: string): Promise<TrackingHistoryResponse> {
    const response = await fetch(`${BACKEND_API}/api/tracking/users`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, turnstileToken })
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw new Error(data.error || 'Failed to start tracking');
    }

    return data;
}

export async function fetchTrackingHistory(username: string): Promise<TrackingHistoryResponse> {
    try {
        const response = await fetch(`${BACKEND_API}/api/tracking/users/${encodeURIComponent(username)}`);
        if (!response.ok) {
            throw new Error('Failed to fetch tracking history');
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching tracking history:', error);
        return { username, history: [], latest: null, forecast: null };
    }
}

export interface ManagedWebSocketConnection {
    close: () => void;
}

export function createWebSocket(
    onMessage: (redemption: Redemption) => void,
    onChatStats?: (count: number) => void,
    onOpen?: () => void,
    onClose?: () => void
): ManagedWebSocketConnection {
    let socket: WebSocket | null = null;
    let reconnectTimeout: number | null = null;
    let stopped = false;

    const connect = () => {
        if (stopped) {
            return;
        }

        const ws = new WebSocket(WS_URL);
        socket = ws;

        ws.onopen = () => {
            onOpen?.();
        };

        ws.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                if (message.type === 'new_redemption') {
                    onMessage(message.data);
                } else if (message.type === 'chat_stats' && onChatStats) {
                    onChatStats(message.data.chatMessages);
                }
            } catch (error) {
                console.error('[WS] Error parsing message:', error);
            }
        };

        ws.onclose = () => {
            if (socket === ws) {
                socket = null;
            }

            if (stopped) {
                return;
            }

            onClose?.();
            reconnectTimeout = window.setTimeout(connect, 3000);
        };

        ws.onerror = (error) => {
            console.error('[WS] Error:', error);
        };
    };

    connect();

    return {
        close() {
            stopped = true;

            if (reconnectTimeout !== null) {
                window.clearTimeout(reconnectTimeout);
                reconnectTimeout = null;
            }

            if (socket) {
                socket.onopen = null;
                socket.onmessage = null;
                socket.onclose = null;
                socket.onerror = null;
                socket.close();
                socket = null;
            }
        }
    };
}
